import {
  type StyleProp,
  View,
  type ViewProps,
  type ViewStyle,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';

import type { UnistylesStyleValue } from './UnistylesStyleValue';

type SpaceValue = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
type SemanticDimension = 'full';

type BoxStyleProps = {
  p?: SpaceValue;
  px?: SpaceValue;
  py?: SpaceValue;
  pt?: SpaceValue;
  pb?: SpaceValue;
  pl?: SpaceValue;
  pr?: SpaceValue;
  m?: SpaceValue;
  mx?: SpaceValue;
  my?: SpaceValue;
  mt?: SpaceValue;
  mb?: SpaceValue;
  ml?: SpaceValue;
  mr?: SpaceValue;
  gap?: SpaceValue;
  w?: SemanticDimension;
  h?: SemanticDimension;
  minW?: SemanticDimension;
  maxW?: SemanticDimension;
  minH?: SemanticDimension;
  maxH?: SemanticDimension;
  width?: UnistylesStyleValue<'width'>;
  height?: UnistylesStyleValue<'height'>;
  minWidth?: UnistylesStyleValue<'minWidth'>;
  maxWidth?: UnistylesStyleValue<'maxWidth'>;
  minHeight?: UnistylesStyleValue<'minHeight'>;
  maxHeight?: UnistylesStyleValue<'maxHeight'>;
  padding?: UnistylesStyleValue<'padding'>;
  paddingHorizontal?: UnistylesStyleValue<'paddingHorizontal'>;
  paddingVertical?: UnistylesStyleValue<'paddingVertical'>;
  paddingTop?: UnistylesStyleValue<'paddingTop'>;
  paddingBottom?: UnistylesStyleValue<'paddingBottom'>;
  paddingLeft?: UnistylesStyleValue<'paddingLeft'>;
  paddingRight?: UnistylesStyleValue<'paddingRight'>;
  margin?: UnistylesStyleValue<'margin'>;
  marginHorizontal?: UnistylesStyleValue<'marginHorizontal'>;
  marginVertical?: UnistylesStyleValue<'marginVertical'>;
  marginTop?: UnistylesStyleValue<'marginTop'>;
  marginBottom?: UnistylesStyleValue<'marginBottom'>;
  marginLeft?: UnistylesStyleValue<'marginLeft'>;
  marginRight?: UnistylesStyleValue<'marginRight'>;
  align?: UnistylesStyleValue<'alignItems'>;
  alignItems?: UnistylesStyleValue<'alignItems'>;
  justify?: UnistylesStyleValue<'justifyContent'>;
  justifyContent?: UnistylesStyleValue<'justifyContent'>;
  overflow?: Extract<UnistylesStyleValue<'overflow'>, 'hidden' | 'visible'>;
  position?: UnistylesStyleValue<'position'>;
  top?: UnistylesStyleValue<'top'>;
  left?: UnistylesStyleValue<'left'>;
  right?: UnistylesStyleValue<'right'>;
  bottom?: UnistylesStyleValue<'bottom'>;
  zIndex?: UnistylesStyleValue<'zIndex'>;
  opacity?: UnistylesStyleValue<'opacity'>;
  flex?: UnistylesStyleValue<'flex'> | boolean;
  flexGrow?: UnistylesStyleValue<'flexGrow'>;
  flexShrink?: UnistylesStyleValue<'flexShrink'>;
  alignSelf?: UnistylesStyleValue<'alignSelf'>;
  wrap?: boolean | UnistylesStyleValue<'flexWrap'>;
  flexWrap?: UnistylesStyleValue<'flexWrap'>;
  backgroundColor?: UnistylesStyleValue<'backgroundColor'>;
  borderColor?: UnistylesStyleValue<'borderColor'>;
  borderTopColor?: UnistylesStyleValue<'borderTopColor'>;
  borderRightColor?: UnistylesStyleValue<'borderRightColor'>;
  borderBottomColor?: UnistylesStyleValue<'borderBottomColor'>;
  borderLeftColor?: UnistylesStyleValue<'borderLeftColor'>;
  borderWidth?: UnistylesStyleValue<'borderWidth'>;
  borderTopWidth?: UnistylesStyleValue<'borderTopWidth'>;
  borderRightWidth?: UnistylesStyleValue<'borderRightWidth'>;
  borderBottomWidth?: UnistylesStyleValue<'borderBottomWidth'>;
  borderLeftWidth?: UnistylesStyleValue<'borderLeftWidth'>;
  borderStyle?: UnistylesStyleValue<'borderStyle'>;
  borderRadius?: UnistylesStyleValue<'borderRadius'>;
  borderTopLeftRadius?: UnistylesStyleValue<'borderTopLeftRadius'>;
  borderTopRightRadius?: UnistylesStyleValue<'borderTopRightRadius'>;
  borderBottomRightRadius?: UnistylesStyleValue<'borderBottomRightRadius'>;
  borderBottomLeftRadius?: UnistylesStyleValue<'borderBottomLeftRadius'>;
};

type BoxVariantProps = {
  background?:
    | 'surface'
    | 'surfaceSecondary'
    | 'surfaceMuted'
    | 'surfaceInverted'
    | 'divider'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    | 'warning'
    | 'error'
    | 'white'
    | 'black';
  border?: 'secondary' | 'muted' | 'divider';
  center?: boolean;
  direction?:
    | 'horizontal'
    | 'horizontal-reverse'
    | 'vertical'
    | 'vertical-reverse';
  space?: SpaceValue;
};
type BoxViewProps = Omit<
  ViewProps,
  keyof BoxStyleProps | keyof BoxVariantProps | 'style'
>;

interface BoxProps extends BoxVariantProps, BoxStyleProps, BoxViewProps {
  style?: StyleProp<ViewStyle>;
}

export default function Box({
  children,
  background,
  border,
  direction,
  space,
  flex,
  flexGrow,
  flexShrink,
  alignSelf,
  flexWrap,
  wrap,
  center,
  align,
  alignItems,
  justify,
  justifyContent,
  position,
  style: extraStyles,
  backgroundColor,
  borderColor,
  borderTopColor,
  borderRightColor,
  borderBottomColor,
  borderLeftColor,
  borderWidth,
  borderTopWidth,
  borderRightWidth,
  borderBottomWidth,
  borderLeftWidth,
  borderStyle,
  borderRadius,
  borderTopLeftRadius,
  borderTopRightRadius,
  borderBottomRightRadius,
  borderBottomLeftRadius,
  p,
  px,
  py,
  pt,
  pb,
  pl,
  pr,
  m,
  mx,
  my,
  mt,
  mb,
  ml,
  mr,
  gap,
  w,
  h,
  minW,
  maxW,
  minH,
  maxH,
  width,
  height,
  minWidth,
  maxWidth,
  minHeight,
  maxHeight,
  padding,
  paddingHorizontal,
  paddingVertical,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  margin,
  marginHorizontal,
  marginVertical,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  overflow,
  top,
  left,
  right,
  bottom,
  zIndex,
  opacity,
  ...rest
}: BoxProps) {
  styles.useVariants({
    background,
    border,
    direction,
    space,
    center,
  });

  return (
    <View
      {...rest}
      style={[
        styles.container({
          p,
          px,
          py,
          pt,
          pb,
          pl,
          pr,
          m,
          mx,
          my,
          mt,
          mb,
          ml,
          mr,
          gap,
          w,
          h,
          minW,
          maxW,
          minH,
          maxH,
          width,
          height,
          minWidth,
          maxWidth,
          minHeight,
          maxHeight,
          padding,
          paddingHorizontal,
          paddingVertical,
          paddingTop,
          paddingBottom,
          paddingLeft,
          paddingRight,
          margin,
          marginHorizontal,
          marginVertical,
          marginTop,
          marginBottom,
          marginLeft,
          marginRight,
          backgroundColor,
          borderColor,
          borderTopColor,
          borderRightColor,
          borderBottomColor,
          borderLeftColor,
          borderWidth,
          borderTopWidth,
          borderRightWidth,
          borderBottomWidth,
          borderLeftWidth,
          borderStyle,
          borderRadius,
          borderTopLeftRadius,
          borderTopRightRadius,
          borderBottomRightRadius,
          borderBottomLeftRadius,
          alignItems: align ?? alignItems,
          justifyContent: justify ?? justifyContent,
          flex,
          flexGrow,
          flexShrink,
          alignSelf,
          flexWrap: normalizeWrap(wrap, flexWrap),
          overflow,
          position,
          top,
          left,
          right,
          bottom,
          zIndex,
          opacity,
        }),
        extraStyles,
      ]}
    >
      {children}
    </View>
  );
}

function normalizeWrap(
  wrap: BoxStyleProps['wrap'],
  flexWrap: BoxStyleProps['flexWrap'],
) {
  if (typeof wrap === 'boolean') {
    return wrap ? 'wrap' : 'nowrap';
  }

  return wrap ?? flexWrap;
}

const semanticDimensions: Record<
  SemanticDimension,
  UnistylesStyleValue<'width'>
> = {
  full: '100%',
};

const styles = StyleSheet.create((theme) => ({
  container: ({
    p,
    m,
    px,
    py,
    pt,
    pb,
    pl,
    pr,
    mx,
    my,
    mt,
    mb,
    ml,
    mr,
    gap,
    w,
    h,
    minW,
    maxW,
    minH,
    maxH,
    width,
    height,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    padding,
    paddingHorizontal,
    paddingVertical,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    margin,
    marginHorizontal,
    marginVertical,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    backgroundColor,
    borderColor,
    borderTopColor,
    borderRightColor,
    borderBottomColor,
    borderLeftColor,
    borderWidth,
    borderTopWidth,
    borderRightWidth,
    borderBottomWidth,
    borderLeftWidth,
    borderStyle,
    borderRadius,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomRightRadius,
    borderBottomLeftRadius,
    alignItems,
    justifyContent,
    flex,
    flexGrow,
    flexShrink,
    alignSelf,
    flexWrap,
    overflow,
    position,
    top,
    left,
    right,
    bottom,
    zIndex,
    opacity,
  }: BoxStyleProps) => ({
    padding: p ? theme.space(p) : padding,
    margin: m ? theme.space(m) : margin,
    paddingHorizontal: px ? theme.space(px) : paddingHorizontal,
    paddingVertical: py ? theme.space(py) : paddingVertical,
    marginHorizontal: mx ? theme.space(mx) : marginHorizontal,
    marginVertical: my ? theme.space(my) : marginVertical,
    paddingTop: pt ? theme.space(pt) : paddingTop,
    paddingBottom: pb ? theme.space(pb) : paddingBottom,
    paddingLeft: pl ? theme.space(pl) : paddingLeft,
    paddingRight: pr ? theme.space(pr) : paddingRight,
    marginTop: mt ? theme.space(mt) : marginTop,
    marginBottom: mb ? theme.space(mb) : marginBottom,
    marginLeft: ml ? theme.space(ml) : marginLeft,
    marginRight: mr ? theme.space(mr) : marginRight,
    gap: gap ? theme.space(gap) : undefined,
    width: w ? semanticDimensions[w] : width,
    height: h ? semanticDimensions[h] : height,
    minWidth: minW ? semanticDimensions[minW] : minWidth,
    maxWidth: maxW ? semanticDimensions[maxW] : maxWidth,
    minHeight: minH ? semanticDimensions[minH] : minHeight,
    maxHeight: maxH ? semanticDimensions[maxH] : maxHeight,
    flex: typeof flex === 'number' ? flex : flex ? 1 : undefined,
    flexGrow,
    flexShrink,
    alignSelf,
    flexWrap,
    overflow,
    alignItems,
    justifyContent,
    backgroundColor,
    borderColor,
    borderTopColor,
    borderRightColor,
    borderBottomColor,
    borderLeftColor,
    borderWidth,
    borderTopWidth,
    borderRightWidth,
    borderBottomWidth,
    borderLeftWidth,
    borderStyle,
    borderRadius,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomRightRadius,
    borderBottomLeftRadius,
    position,
    top,
    left,
    right,
    bottom,
    zIndex,
    opacity,
    variants: {
      center: {
        true: {
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
      background: {
        surface: {
          backgroundColor: theme.colors.surface,
        },
        surfaceSecondary: {
          backgroundColor: theme.colors.surfaceSecondary,
        },
        surfaceMuted: {
          backgroundColor: theme.colors.surfaceMuted,
        },
        surfaceInverted: {
          backgroundColor: theme.colors.surfaceInverted,
        },
        divider: {
          backgroundColor: theme.colors.divider,
        },
        primary: {
          backgroundColor: theme.colors.primary,
        },
        secondary: {
          backgroundColor: theme.colors.secondary,
        },
        success: {
          backgroundColor: theme.colors.success,
        },
        info: {
          backgroundColor: theme.colors.info,
        },
        warning: {
          backgroundColor: theme.colors.warning,
        },
        error: {
          backgroundColor: theme.colors.error,
        },
        white: {
          backgroundColor: theme.colors.white,
        },
        black: {
          backgroundColor: theme.colors.black,
        },
      },
      border: {
        secondary: {
          borderColor: theme.colors.borderSecondary,
        },
        muted: {
          borderColor: theme.colors.borderMuted,
        },
        divider: {
          borderColor: theme.colors.dividerStrong,
        },
      },
      direction: {
        horizontal: {
          flexDirection: 'row',
        },
        'horizontal-reverse': {
          flexDirection: 'row-reverse',
        },
        vertical: {
          flexDirection: 'column',
        },
        'vertical-reverse': {
          flexDirection: 'column-reverse',
        },
      },
      space: {
        xs: {
          gap: theme.space('xs'),
        },
        sm: {
          gap: theme.space('sm'),
        },
        md: {
          gap: theme.space('md'),
        },
        lg: {
          gap: theme.space('lg'),
        },
        xl: {
          gap: theme.space('xl'),
        },
        '2xl': {
          gap: theme.space('2xl'),
        },
        '3xl': {
          gap: theme.space('3xl'),
        },
        '4xl': {
          gap: theme.space('4xl'),
        },
      },
    },
  }),
}));
